/* eslint-disable react-refresh/only-export-components */
import React, { useState, useEffect } from 'react'
import { useThemeColors } from '../hooks/useThemeColors'
import { Cookie, X } from 'lucide-react'

const COOKIE_CONSENT_KEY = 'cookieConsent'

export type CookieConsentValue = 'all' | 'necessary' | null

export function getCookieConsent(): CookieConsentValue {
  try {
    const value = localStorage.getItem(COOKIE_CONSENT_KEY)
    if (value === 'all' || value === 'necessary') {
      return value
    }
    return null
  } catch {
    return null
  }
}

export function hasFullConsent(): boolean {
  return getCookieConsent() === 'all'
}

export default function CookieConsent() {
  const { colors } = useThemeColors()
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    // Check if user has already consented
    const consent = getCookieConsent()
    if (!consent) {
      // Small delay to avoid showing immediately on page load
      const timer = setTimeout(() => {
        setIsVisible(true)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [])

  const handleConsent = (type: 'all' | 'necessary') => {
    try {
      localStorage.setItem(COOKIE_CONSENT_KEY, type)
      setIsVisible(false)

      // If full consent, Sentry replay can be enabled
      // Note: Sentry is initialized at startup, so this stores the preference
      // for future session replay decisions
      if (type === 'all') {
        // Dispatch event for any listeners that need to know about consent
        window.dispatchEvent(new CustomEvent('cookieConsentGranted', {
          detail: { type }
        }))
      }
    } catch (error) {
      console.error('Failed to save cookie consent:', error)
    }
  }

  if (!isVisible) {
    return null
  }

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        padding: '1rem',
        background: 'linear-gradient(to top, rgba(0,0,0,0.1) 0%, transparent 100%)'
      }}
    >
      <div
        style={{
          maxWidth: '680px',
          margin: '0 auto',
          backgroundColor: '#FFFFFF',
          borderRadius: '12px',
          padding: '1.25rem 1.5rem',
          boxShadow: '0 4px 24px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.05)',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem'
        }}
      >
        {/* Content Row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.875rem' }}>
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              backgroundColor: colors.primary ? `${colors.primary}15` : '#3B82F615',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <Cookie
              size={20}
              style={{ color: colors.primary || '#3B82F6' }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <p style={{
              color: '#1F2937',
              fontSize: '0.9rem',
              lineHeight: 1.5,
              margin: 0
            }}>
              We use cookies to enhance your experience. By clicking "Accept All", you consent
              to analytics and session replay to help us improve our service.
            </p>
            <a
              href="/privacy"
              style={{
                color: colors.primary || '#3B82F6',
                fontSize: '0.85rem',
                marginTop: '0.375rem',
                display: 'inline-block',
                textDecoration: 'none'
              }}
              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
            >
              Learn more in our Privacy Policy
            </a>
          </div>
          {/* Close button - desktop only */}
          <button
            onClick={() => handleConsent('necessary')}
            style={{
              padding: '0.375rem',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: 'transparent',
              color: '#9CA3AF',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#F3F4F6'
              e.currentTarget.style.color = '#6B7280'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent'
              e.currentTarget.style.color = '#9CA3AF'
            }}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Buttons Row */}
        <div style={{
          display: 'flex',
          gap: '0.75rem',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={() => handleConsent('necessary')}
            style={{
              padding: '0.625rem 1.25rem',
              borderRadius: '8px',
              fontSize: '0.875rem',
              fontWeight: 500,
              border: '1px solid #E5E7EB',
              backgroundColor: '#FFFFFF',
              color: '#374151',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#F9FAFB'
              e.currentTarget.style.borderColor = '#D1D5DB'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#FFFFFF'
              e.currentTarget.style.borderColor = '#E5E7EB'
            }}
          >
            Necessary Only
          </button>
          <button
            onClick={() => handleConsent('all')}
            style={{
              padding: '0.625rem 1.5rem',
              borderRadius: '8px',
              fontSize: '0.875rem',
              fontWeight: 500,
              border: 'none',
              backgroundColor: colors.primary || '#3B82F6',
              color: '#FFFFFF',
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.9'
              e.currentTarget.style.transform = 'translateY(-1px)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1'
              e.currentTarget.style.transform = 'translateY(0)'
            }}
          >
            Accept All
          </button>
        </div>
      </div>
    </div>
  )
}
